import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import { HardHat, Loader2, Search, MapPin, Trophy, X, Send } from "lucide-react";

const STATUS_META = {
  proposto: { label: "Proposto", cls: "border-amber-500/40 text-amber-500" },
  accettato: { label: "Ha accettato", cls: "border-blue-500/40 text-blue-500" },
  rifiutato: { label: "Ha rifiutato", cls: "border-red-500/40 text-red-500" },
  selezionato: { label: "Selezionato", cls: "border-success/40 text-success" },
  non_selezionato: { label: "Non selezionato", cls: "border-muted-foreground/40 text-muted-foreground" },
};

export function AssignInstallersDialog({ lead, open, onOpenChange, onChanged }) {
  const [assigned, setAssigned] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!lead) return;
    setLoading(true);
    try {
      const [a, s] = await Promise.all([
        api.get(`/leads/${lead.id}/installers`),
        api.get(`/leads/${lead.id}/installers/suggested`),
      ]);
      setAssigned(a.data.assigned || []);
      setSuggested(s.data.items || []);
      setMeta({ provincia: s.data.provincia, regione: s.data.regione, has_coords: s.data.has_coords });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore caricamento installatori");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (open) { setSel({}); setQ(""); load(); } /* eslint-disable-next-line */ }, [open, lead?.id]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return suggested.filter((i) => !i.gia_assegnato && (!s || [i.ragione_sociale, i.comune, i.provincia, i.agenzia].filter(Boolean).join(" ").toLowerCase().includes(s)));
  }, [suggested, q]);

  const selIds = Object.keys(sel).filter((k) => sel[k]);

  const activate = async () => {
    if (selIds.length === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/installers`, { installer_ids: selIds });
      setAssigned(data.assigned || []);
      setSel({});
      await load();
      onChanged && onChanged();
      toast.success(`${data.added} installatori attivati — email inviate al cliente e ai partner`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore attivazione");
    } finally { setBusy(false); }
  };

  const setStatus = async (installer_id, status) => {
    try {
      const { data } = await api.patch(`/leads/${lead.id}/installers/${installer_id}`, { status });
      setAssigned(data.assigned || []);
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore aggiornamento"); }
  };
  const remove = async (installer_id) => {
    try {
      const { data } = await api.delete(`/leads/${lead.id}/installers/${installer_id}`);
      setAssigned(data.assigned || []);
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error("Errore rimozione"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="assign-installers-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><HardHat className="h-5 w-5 text-primary" /> Installatori in gara — {lead?.name}</DialogTitle>
          <DialogDescription>
            Attiva uno o più installatori partner: riceveranno la consulenza per valutare se proporsi. Il cliente riceve la comunicazione, ogni installatore una copia individuale.
          </DialogDescription>
        </DialogHeader>

        {assigned.length > 0 && (
          <div className="space-y-2" data-testid="assigned-installers">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Attivati ({assigned.length})</div>
            {assigned.map((i) => {
              const m = STATUS_META[i.status] || STATUS_META.proposto;
              return (
                <div key={i.installer_id} className="flex items-center justify-between gap-2 p-2 rounded border border-border" data-testid={`assigned-${i.installer_id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{i.ragione_sociale}</div>
                    <div className="text-xs text-muted-foreground">{[i.comune, i.provincia].filter(Boolean).join(", ")}{i.agenzia ? ` · ${i.agenzia}` : ""}</div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Badge variant="outline" className={`text-[11px] font-normal ${m.cls}`}>{m.label}</Badge>
                    {i.status !== "selezionato" && (
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-success" onClick={() => setStatus(i.installer_id, "selezionato")} data-testid={`select-${i.installer_id}`} title="Seleziona per il lavoro">
                        <Trophy className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-destructive" onClick={() => remove(i.installer_id)} data-testid={`remove-${i.installer_id}`} title="Rimuovi">
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">
              Installatori disponibili {meta.provincia ? `· zona ${meta.provincia}` : ""}
            </div>
            <div className="relative w-48">
              <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca…" className="pl-8 h-8" data-testid="installer-search" />
            </div>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Caricamento…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-6 text-sm text-muted-foreground">Nessun installatore disponibile.</div>
          ) : (
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {filtered.map((i) => (
                <label key={i.installer_id} className="flex items-center gap-3 p-2 rounded hover:bg-secondary/50 cursor-pointer" data-testid={`suggest-${i.installer_id}`}>
                  <Checkbox checked={!!sel[i.installer_id]} onCheckedChange={(v) => setSel((s) => ({ ...s, [i.installer_id]: !!v }))} data-testid={`suggest-check-${i.installer_id}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{i.ragione_sociale} {i.in_zona && <Badge variant="secondary" className="ml-1 text-[10px] font-normal">in zona</Badge>}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-2">
                      <span>{[i.comune, i.provincia].filter(Boolean).join(", ")}</span>
                      {i.distanza_km != null && <span className="flex items-center gap-0.5"><MapPin className="h-3 w-3" /> {i.distanza_km} km</span>}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="close-installers">Chiudi</Button>
          <Button onClick={activate} disabled={busy || selIds.length === 0} data-testid="activate-installers">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Attiva {selIds.length > 0 ? `(${selIds.length})` : ""} e invia
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
